import React, { useEffect, useState } from 'react';
import './App.css';
import { io } from 'socket.io-client';
import { Sparkles, AlertTriangle, Smartphone, X, ShieldCheck } from 'lucide-react'; // أيقونات احترافية
import BottomNav from './components/BottomNav';
import UserProfile from './components/UserProfile';
import Match from './components/Match';
import Friends from './components/Friends';

import Profile from './components/Profile';
import NicknamePrompt from './components/NicknamePrompt';
import AdminPanel from './components/AdminPanel';
import SubscriptionModal from './components/SubscriptionModal';
import ConnectionStatus from './components/ConnectionStatus';
import AnonymousChat from './components/AnonymousChat';
import { ChatProvider } from './context/ChatContext';
import { AdBanner } from './components/AdBanner';
import { triggerHaptic } from './utils/haptics';

function App() {
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem('hams_cached_user');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });

  const [authStatus, setAuthStatus] = useState(() => {
    try {
      const cached = localStorage.getItem('hams_cached_user');
      return cached ? 'subscribed' : 'loading';
    } catch {
      return 'loading';
    }
  }); // loading, subscribed, error, needs_subscription

  const [missingChannels, setMissingChannels] = useState([]);

  const [isSubscribed, setIsSubscribed] = useState(() => {
    try {
      const cached = localStorage.getItem('hams_cached_is_subscribed');
      return cached ? JSON.parse(cached) : true;
    } catch {
      return true;
    }
  });

  const [activeTab, setActiveTab] = useState('match');
  const [deepLinkTarget, setDeepLinkTarget] = useState(null);
  const [anonDeepLinkCode, setAnonDeepLinkCode] = useState(null);
  const [showSubscriptionOverlay, setShowSubscriptionOverlay] = useState(false);
  const [deepLinkProfileUserId, setDeepLinkProfileUserId] = useState(null);
  const [totalUnreadFriends, setTotalUnreadFriends] = useState(0);
  const [chatWithFriendId, setChatWithFriendId] = useState(null);

  const [vipSettings, setVipSettings] = useState(() => {
    try {
      const cached = localStorage.getItem('hams_cached_vip_settings');
      return cached ? JSON.parse(cached) : { vipStarPrice: 50, vipPerks: [] };
    } catch {
      return { vipStarPrice: 50, vipPerks: [] };
    }
  });

  const [currentTheme, setCurrentTheme] = useState(() => {
    return localStorage.getItem('hams_app_theme') || 'theme-premium-dark';
  });

  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('hams_app_theme', currentTheme);
  }, [currentTheme]);

  useEffect(() => {
    let timeoutId;
    const handleFocus = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        clearTimeout(timeoutId);
        setIsKeyboardOpen(true);
      }
    };
    const handleBlur = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        timeoutId = setTimeout(() => {
          setIsKeyboardOpen(false);
        }, 100);
      }
    };

    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
    };
  }, []);

  useEffect(() => {
    const resolveUsernameDeepLink = async (username) => {
      try {
        const response = await fetch(`/api/users/search/by-username/${username}`);
        if (response.ok) {
          const data = await response.json();
          if (data.telegramId) {
            setDeepLinkProfileUserId(data.telegramId);
          }
        } else {
          console.error('User not found for deep link:', username);
        }
      } catch (err) {
        console.error('Error resolving deep link:', err);
      }
    };

    const authenticate = async () => {
      const tg = window.Telegram?.WebApp;
      if (!tg) {
        setAuthStatus('error');
        return;
      }

      // Prevent swiping down to close Web App
      if (typeof tg.disableVerticalSwipes === 'function') {
        tg.disableVerticalSwipes();
      } else {
        tg.isVerticalSwipesEnabled = false;
      }

      // Support active tab switching via query parameter
      const urlParams = new URLSearchParams(window.location.search);
      const queryTab = urlParams.get('tab');
      if (queryTab) {
        setActiveTab(queryTab);
      }
      const chatWith = urlParams.get('chat_with');
      if (chatWith) {
        setChatWithFriendId(chatWith);
        setActiveTab('friends');
      }

      const tgInitData = tg.initData;
      const tgUser = tg.initDataUnsafe?.user;
      const startParam = tg.initDataUnsafe?.start_param;

      if (startParam) {
          // التعامل مع الروابط المجهولة
          if (startParam.startsWith('usr_')) {
              setAnonDeepLinkCode(startParam.replace('usr_', ''));
              setActiveTab('anonymous');
          } else if (startParam.startsWith('me_')) {
              const targetUsername = startParam.substring(3);
              resolveUsernameDeepLink(targetUsername);
          } else if (startParam.startsWith('group_')) {
              setActiveTab('friends');
          } else {
              setActiveTab('friends');
          }
      }

      if (!tgInitData || !tgUser) {
        if (process.env.NODE_ENV === 'development') {
           return mockAuth();
        }
        setAuthStatus('error');
        return;
      }

      try {
        const response = await fetch('/api/auth/telegram', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ initData: tgInitData, user: tgUser })
        });

        if (response.status === 403) {
          const data = await response.json();
          if (data.error === 'Banned') {
              alert(data.message);
              setAuthStatus('error');
              localStorage.removeItem('hams_cached_user');
              localStorage.removeItem('hams_cached_is_subscribed');
              localStorage.removeItem('hams_cached_vip_settings');
              return;
          }
        }

        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          setIsSubscribed(data.isSubscribed);
          if (data.vipSettings) setVipSettings(data.vipSettings);

          localStorage.setItem('hams_cached_user', JSON.stringify(data.user));
          localStorage.setItem('hams_cached_is_subscribed', JSON.stringify(data.isSubscribed));
          if (data.vipSettings) {
              localStorage.setItem('hams_cached_vip_settings', JSON.stringify(data.vipSettings));
          }

          if (!data.isSubscribed && data.missingChannels && data.missingChannels.length > 0) {
              setMissingChannels(data.missingChannels);
          }
          setAuthStatus('subscribed'); // ندخل دائماً للتطبيق بدون حجب كامل في الواجهة
        } else {
          if (response.status === 401 || response.status === 403) {
             setAuthStatus('error');
             localStorage.removeItem('hams_cached_user');
             localStorage.removeItem('hams_cached_is_subscribed');
             localStorage.removeItem('hams_cached_vip_settings');
          } else {
             if (!localStorage.getItem('hams_cached_user')) {
                setAuthStatus('error');
             }
          }
        }
      } catch (error) {
        console.error('Failed to authenticate:', error);
        if (!localStorage.getItem('hams_cached_user')) {
           setAuthStatus('error');
        }
      }
    };

    authenticate();
  }, []);

  const mockAuth = async () => {
      try {
        const response = await fetch('/api/auth/telegram', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user: { id: 12345, username: 'testuser', first_name: 'Test' } })
        });
        if (response.ok) {
           const data = await response.json();
           setUser(data.user);
           setIsSubscribed(data.isSubscribed !== undefined ? data.isSubscribed : true);
           if (data.vipSettings) setVipSettings(data.vipSettings);

           localStorage.setItem('hams_cached_user', JSON.stringify(data.user));
           localStorage.setItem('hams_cached_is_subscribed', JSON.stringify(data.isSubscribed !== undefined ? data.isSubscribed : true));
           if (data.vipSettings) {
               localStorage.setItem('hams_cached_vip_settings', JSON.stringify(data.vipSettings));
           }

           if (!data.isSubscribed && data.missingChannels && data.missingChannels.length > 0) {
              setMissingChannels(data.missingChannels);
           }
           setAuthStatus('subscribed'); // ندخل دائماً للتطبيق بدون حجب كامل في الواجهة
        } else {
           if (!localStorage.getItem('hams_cached_user')) {
              setAuthStatus('error');
           }
        }
      } catch (err) {
         if (!localStorage.getItem('hams_cached_user')) {
            setAuthStatus('error');
         }
      }
  };

  const handleVerifySubscriptionStatus = async () => {
      const tg = window.Telegram?.WebApp;
      const tgInitData = tg?.initData || '';
      const tgUser = tg?.initDataUnsafe?.user;

      try {
          const response = await fetch('/api/auth/telegram', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                  initData: tgInitData, 
                  user: tgUser || (process.env.NODE_ENV === 'development' ? { id: 12345, username: 'testuser', first_name: 'Test' } : null) 
              })
          });

          if (response.ok) {
              const data = await response.json();
              setIsSubscribed(data.isSubscribed);
              setMissingChannels(data.missingChannels || []);
              if (data.isSubscribed) {
                  setShowSubscriptionOverlay(false);
                  alert('🎉 تم تأكيد اشتراكك بنجاح! تم تفعيل استخدام الميزات.');
              } else {
                  alert('❌ لم تشترك في كل القنوات المطلوبة بعد. يرجى الاشتراك في القنوات ثم الضغط مجدداً.');
              }
          }
      } catch (err) {
          console.error(err);
          alert('حدث خطأ في الاتصال');
      }
  };

  // شاشة التحميل
  if (authStatus === 'loading') {
    return (
      <div className="fullscreen-state">
        <div className="loader-pulse"></div>
        <p>جاري الاتصال بالسيرفر...</p>
      </div>
    );
  }

  // شاشة الخطأ
  if (authStatus === 'error') {
    return (
      <div className="fullscreen-state">
        <AlertTriangle size={64} className="text-danger" color="#f43f5e" />
        <h2 className="error-title">فشل المصادقة</h2>
        <p className="error-desc">يرجى فتح التطبيق عبر تيليجرام أو التأكد من اتصالك بالإنترنت.</p>
      </div>
    );
  }

  // لقد ألغينا حجب شاشة الاشتراك عند تسجيل الدخول
  // لكي تظهر فقط كنافذة عائمة (Overlay) عند استخدام ميزات محددة.

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('hams_cached_user', JSON.stringify(updatedUser));
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'match': return <Match user={user} isSubscribed={isSubscribed} onRequestSubscription={() => setShowSubscriptionOverlay(true)} />;
      case 'friends': return <Friends user={user} isSubscribed={isSubscribed} onRequestSubscription={() => setShowSubscriptionOverlay(true)} vipSettings={vipSettings} onUnreadChange={setTotalUnreadFriends} chatWithFriendId={chatWithFriendId} onClearChatWithFriend={() => setChatWithFriendId(null)} />;

      case 'profile': return <Profile user={user} onUserUpdate={handleUserUpdate} vipSettings={vipSettings} />;
      case 'admin': return <AdminPanel user={user} />;
      case 'anonymous': return (
        <AnonymousChat
          user={user}
          deepLinkCode={anonDeepLinkCode}
          isSubscribed={isSubscribed}
          onRequestSubscription={() => setShowSubscriptionOverlay(true)}
          onBack={() => {
            setAnonDeepLinkCode(null);
            setActiveTab('friends');
          }}
        />
      );
      default: return <Match user={user} isSubscribed={isSubscribed} />;
    }
  };

  const handleNicknameComplete = (updatedUser) => {
    handleUserUpdate(updatedUser);
  };

  return (
    <div className={`app-container ${currentTheme} ${isKeyboardOpen ? 'keyboard-open' : ''}`}>
      <ChatProvider user={user}>
          {/* نافذة اختيار الاسم المستعار (تظهر كطبقة علوية) */}
          {authStatus === 'subscribed' && user && (user.nickname === null || user.nickname === undefined) && (
            <NicknamePrompt user={user} onComplete={handleNicknameComplete} />
          )}

          {/* تنبيه حالة الاتصال */}
          <ConnectionStatus />

          {/* الهيدر العلوي الجديد والأنيق */}
          <header className="app-top-header">
            <div className="header-right-brand">
              <h1 className="welcome-text" onClick={() => { triggerHaptic('light'); setActiveTab('match'); }} style={{ cursor: 'pointer' }}>
                <Sparkles size={18} className="welcome-icon" />
                <span>سرّك</span>
              </h1>
            </div>

            <div className="header-left-actions">
              {/* مبدل السمات الصغير جداً */}
              <div className="theme-selector-toggle-mini">
                <button
                  className={`theme-opt-btn-mini ${currentTheme === 'theme-premium-dark' ? 'active' : ''}`}
                  onClick={() => {
                    triggerHaptic('light');
                    setCurrentTheme('theme-premium-dark');
                  }}
                  title="السمة الافتراضية"
                >
                  <Sparkles size={12} />
                </button>

                <button
                  className={`theme-opt-btn-mini ${currentTheme === 'theme-tg-native' ? 'active' : ''}`}
                  onClick={() => {
                    triggerHaptic('light');
                    setCurrentTheme('theme-tg-native');
                  }}
                  title="سمة تلجرام"
                >
                  <Smartphone size={12} />
                </button>
              </div>

              {/* زر اختصار الإدارة (للأدمن فقط) */}
              {user?.isAdmin && (
                <button
                  className={`header-shortcut-btn ${activeTab === 'admin' ? 'active' : ''}`}
                  onClick={() => {
                    triggerHaptic('light');
                    setActiveTab('admin');
                  }}
                  title="لوحة الإدارة"
                >
                  <ShieldCheck size={16} />
                </button>
              )}

              {/* زر الأفاتار الدائري الأنيق لحسابي */}
              <div 
                className={`header-profile-avatar-wrapper ${activeTab === 'profile' ? 'active' : ''} ${user?.isVip ? 'vip-gold' : ''}`}
                onClick={() => {
                  triggerHaptic('light');
                  setActiveTab('profile');
                }}
                title="إعدادات حسابي"
              >
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt="Avatar" className="header-profile-avatar" />
                ) : (
                  <div className="header-profile-avatar-placeholder">
                    {user?.nickname ? user.nickname.charAt(0) : '👤'}
                  </div>
                )}
                {user?.isVip && <span className="header-profile-vip-crown">👑</span>}
              </div>
            </div>
          </header>

          {/* الشريط الإعلاني المميز أعلى الشاشة لغير المشتركين بـ VIP */}
          <AdBanner currentUser={user} vipSettings={vipSettings} />

          {/* منطقة المحتوى المتغير بناءً على التبويب النشط */}
          <main className="main-content-area">
            {renderTab()}
          </main>

        {activeTab !== 'anonymous' && !isKeyboardOpen && (
          <BottomNav activeTab={activeTab} setActiveTab={setActiveTab} isAdmin={user?.isAdmin} friendsUnreadCount={totalUnreadFriends} />
        )}

        {showSubscriptionOverlay && (
          <div className="subscription-overlay-container">
              <div className="subscription-overlay-content">
                  <button className="close-overlay-btn" onClick={() => setShowSubscriptionOverlay(false)}>
                      <X size={20} />
                  </button>
                  <SubscriptionModal
                      missingChannels={missingChannels}
                      onVerify={handleVerifySubscriptionStatus}
                  />
              </div>
          </div>
        )}

        {/* عرض الملف الشخصي عند فتح التطبيق عبر رابط مشاركة اليوزر */}
        {deepLinkProfileUserId && (
          <UserProfile
            userId={deepLinkProfileUserId}
            onClose={() => setDeepLinkProfileUserId(null)}
          />
        )}
      </ChatProvider>
    </div>
  );
}

export default App;
